import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  DollarSign, Plus, TrendingUp, TrendingDown, Building2,
  Calendar, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseBudgets = () => {
  const navigate = useNavigate();
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBudget, setNewBudget] = useState({
    department: "",
    period: new Date().getFullYear() + "-Q" + Math.ceil((new Date().getMonth() + 1) / 3),
    amount: 0
  });
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem('enterprise_token');
  const user = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    fetchBudgets();
  }, [token, navigate]);

  const fetchBudgets = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/budgets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgetData(response.data);
    } catch (error) {
      console.error("Error fetching budgets:", error);
      if (error.response?.status === 403) {
        toast.error("Ou pa gen aksè pou wè bidjè yo");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API}/enterprise/budgets`, newBudget, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Bidjè kreye!");
      setShowCreateModal(false);
      setNewBudget({ department: "", period: "", amount: 0 });
      fetchBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan kreye bidjè");
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-HT').format(amount) + ' HTG';
  };

  const getUsagePercentage = (used, total) => {
    if (!total) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const canManageBudget = ['finance', 'admin', 'super_admin'].includes(user.role);

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-budgets">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">Bidjè</h1>
            <p className="text-gray-500">Swiv ak jere bidjè antrepriz la</p>
          </div>
          
          {canManageBudget && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold"
              data-testid="create-budget-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Kreye Bidjè
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            {/* Company Budget Overview */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-[#232F3E] to-[#37475A]">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <p className="text-gray-300 mb-2">Bidjè Total</p>
                    <p className="text-4xl font-bold text-white">
                      {formatCurrency(budgetData?.company_budget?.total || 0)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-300 mb-2">Itilize</p>
                    <p className="text-4xl font-bold text-[#FF9900]">
                      {formatCurrency(budgetData?.company_budget?.used || 0)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-300 mb-2">Rete</p>
                    <p className="text-4xl font-bold text-green-400">
                      {formatCurrency(budgetData?.company_budget?.remaining || 0)}
                    </p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-8">
                  <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${getUsageColor(
                        getUsagePercentage(
                          budgetData?.company_budget?.used || 0,
                          budgetData?.company_budget?.total || 1
                        )
                      )}`}
                      style={{ 
                        width: `${getUsagePercentage(
                          budgetData?.company_budget?.used || 0,
                          budgetData?.company_budget?.total || 1
                        )}%` 
                      }}
                    />
                  </div>
                  <p className="text-center text-white/70 mt-2">
                    {getUsagePercentage(
                      budgetData?.company_budget?.used || 0,
                      budgetData?.company_budget?.total || 1
                    ).toFixed(1)}% itilize
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Department Budgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(budgetData?.department_budgets || []).length === 0 ? (
                <Card className="col-span-full border-0 shadow-lg">
                  <CardContent className="p-12 text-center">
                    <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-xl font-semibold text-gray-600">Pa gen bidjè depatman</p>
                    <p className="text-gray-400">Kreye bidjè pou diferan depatman yo</p>
                  </CardContent>
                </Card>
              ) : (
                budgetData.department_budgets.map((budget) => {
                  const usagePercent = getUsagePercentage(budget.used, budget.allocated);
                  
                  return (
                    <Card key={budget.id} className="border-0 shadow-lg" data-testid={`budget-${budget.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-[#FF9900]" />
                            {budget.department || "Jeneral"}
                          </CardTitle>
                          {usagePercent >= 90 && (
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {budget.period}
                        </p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Aloke:</span>
                            <span className="font-bold">{formatCurrency(budget.allocated)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Itilize:</span>
                            <span className="font-bold text-[#FF9900]">{formatCurrency(budget.used)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Rete:</span>
                            <span className="font-bold text-green-600">{formatCurrency(budget.remaining)}</span>
                          </div>
                          
                          {/* Progress */}
                          <div className="pt-2">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all ${getUsageColor(usagePercent)}`}
                                style={{ width: `${usagePercent}%` }}
                              />
                            </div>
                            <p className="text-right text-xs text-gray-500 mt-1">
                              {usagePercent.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Create Budget Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#FF9900]" />
                  Kreye Nouvo Bidjè
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleCreateBudget} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Depatman (Opsyonèl)</Label>
                    <Input
                      value={newBudget.department}
                      onChange={(e) => setNewBudget({...newBudget, department: e.target.value})}
                      placeholder="IT, Marketing, HR..."
                      data-testid="budget-department"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Peryòd</Label>
                    <Input
                      value={newBudget.period}
                      onChange={(e) => setNewBudget({...newBudget, period: e.target.value})}
                      placeholder="2024-Q1"
                      required
                      data-testid="budget-period"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Montan (HTG)</Label>
                    <Input
                      type="number"
                      value={newBudget.amount}
                      onChange={(e) => setNewBudget({...newBudget, amount: parseFloat(e.target.value)})}
                      required
                      min={0}
                      data-testid="budget-amount"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateModal(false)}
                      className="flex-1"
                    >
                      Anile
                    </Button>
                    <Button
                      type="submit"
                      disabled={creating}
                      className="flex-1 bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    >
                      {creating ? "Ap kreye..." : "Kreye"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

